import { useCallback, useRef } from 'react';

type FieldCallback = {
  getter: () => string;
  setter: (value: string) => void;
};

export default function useMarkdownField() {
  const activeFieldElementRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const activeFieldCallbacksRef = useRef<FieldCallback | null>(null);
  const activeFieldSelectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  const getTarget = useCallback(() => activeFieldElementRef.current, []);

  const handleAddChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const activeElement = activeFieldElementRef.current;
    if (!activeElement) return;

    const callbacks = activeFieldCallbacksRef.current;
    if (!callbacks) return;

    callbacks.setter(activeElement.value);
  }, []);

  const registerField = useCallback(
    (getter: () => string, setter: (value: string) => void) =>
      (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const targetElement = (event.currentTarget instanceof HTMLInputElement || event.currentTarget instanceof HTMLTextAreaElement)
          ? event.currentTarget
          : (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
            ? event.target
            : null;

        if (!targetElement) return;

        activeFieldElementRef.current = targetElement;
        activeFieldCallbacksRef.current = { getter, setter };
        activeFieldSelectionRef.current = {
          start: targetElement.selectionStart ?? 0,
          end: targetElement.selectionEnd ?? 0,
        };

        const updateSelection = () => {
          activeFieldSelectionRef.current = {
            start: targetElement.selectionStart ?? 0,
            end: targetElement.selectionEnd ?? 0,
          };
        };

        targetElement.addEventListener('select', updateSelection);
        targetElement.addEventListener('mouseup', updateSelection);
        targetElement.addEventListener('keyup', updateSelection);

        const cleanup = () => {
          targetElement.removeEventListener('select', updateSelection);
          targetElement.removeEventListener('mouseup', updateSelection);
          targetElement.removeEventListener('keyup', updateSelection);
        };

        targetElement.addEventListener('blur', cleanup, { once: true });
      },
    []
  );

  const getSelection = useCallback(() => {
    return activeFieldSelectionRef.current;
  }, []);

  const setSelection = useCallback((selection: { start: number; end: number }) => {
    activeFieldSelectionRef.current = selection;
  }, []);

  return {
    getTarget,
    handleAddChange,
    registerField,
    getSelection,
    setSelection,
  };
}
